/* eslint-disable @typescript-eslint/naming-convention */
import { cognitoAuthConfig, signOutRedirect as cognitoSignOutRedirect } from "./cognitoConfig";
import { entraAuthConfig, entraSignOutRedirect } from "./entraConfig";
import { googleAuthConfig, googleSignOutRedirect } from "./googleConfig";

// Identity provider switch. Build-time flag from .env:
//   cognito (AWS) | entra (Azure) | google (GCP)
// Local dev cannot tell them apart by hostname, so we select by env flag.
// @ts-ignore
const IDP: string = process.env.XEC_IDP || "cognito";

// @ts-ignore
export const isEntra = IDP === "entra";
export const isGoogle = IDP === "google";

// The distinction that actually matters to callers is NOT which of the three
// IdPs it is — it is whether the deployment puts a GATEWAY in front that owns
// the license context.
//
//   cognito (AWS)  the user token goes in X-Identity-Token; no license header;
//                  /internal/v1/me behaves differently (see request.ts).
//   entra (Azure)  APIM validates the JWT and the SPA must send
//                  `Authorization: Bearer` + `X-License-Id` (ADR-008).
//   google (GCP)   the M2 middleware validates the JWT in-process and reads the
//                  same two headers. Verified against
//                  platforms/gcp/middleware/identity_headers.py, whose
//                  base_headers are "authorization" and "x-license-id" — i.e.
//                  byte-identical to the Azure contract.
//
// So GCP is not a third branch: it is the second one. Every existing `isEntra`
// call site wanted THIS predicate, and reading it as "is Azure" is what would
// make a reviewer add a third arm that does the same thing.
export const isGatewayIdp = isEntra || isGoogle;

// OIDC config passed to <AuthProvider>. Shapes differ slightly but react-oidc-context
// ignores extra fields, so spreading any of them is safe.
export const authConfig = isGoogle
  ? googleAuthConfig
  : isEntra
    ? entraAuthConfig
    : cognitoAuthConfig;

// Logout genuinely IS three-way — this is the one place the IdPs diverge rather
// than the gateway. Cognito /logout, Entra /oauth2/v2.0/logout, and Google
// (local only; see googleConfig for why we must not call Google's).
export const signOutRedirect = isGoogle
  ? googleSignOutRedirect
  : isEntra
    ? entraSignOutRedirect
    : cognitoSignOutRedirect;

// ---- Active license (X-License-Id source) ----
// Azure/APIM requires X-License-Id on every management call (ADR-008) for ALL users.
// The per-user license comes purely from GET /me (access_summary.default_license_id) and
// is seeded after login (see useEnsureLicense). Persisted so it survives refresh and can
// be read synchronously here. There is no hardcoded fallback: a caller with no license
// assignment gets a 403 from /me and is routed to the contact-IT page (see App.tsx).
const LICENSE_STORAGE_KEY = "xecguard:active-license-id";

const readStoredLicense = (): string | null => {
  try {
    return window.localStorage.getItem(LICENSE_STORAGE_KEY);
  } catch {
    return null;
  }
};

let activeLicenseId: string | null = readStoredLicense();

export function setActiveLicenseId(id: string | null): void {
  activeLicenseId = id || null;
  try {
    if (id) window.localStorage.setItem(LICENSE_STORAGE_KEY, id);
    else window.localStorage.removeItem(LICENSE_STORAGE_KEY);
  } catch {
    /* ignore storage failures */
  }
}

export function getActiveLicenseId(): string | null {
  return activeLicenseId || readStoredLicense() || null;
}

// Wipe app-local auth state that react-oidc's removeUser() leaves behind: the active
// license id (else the next user on this browser inherits a stale X-License-Id before
// their /me resolves) and any stray oidc.* entries (mirrors the 401 sweep in request.ts).
export function clearLocalAuthState(): void {
  setActiveLicenseId(null);
  try {
    Object.keys(window.sessionStorage)
      .filter((key) => key.startsWith("oidc."))
      .forEach((key) => window.sessionStorage.removeItem(key));
  } catch {
    /* ignore storage failures */
  }
}

// Full logout, shared by the header menu and the /logout route: clear the OIDC user,
// wipe app-local state, then redirect to the IdP sign-out endpoint. removeUser() is
// passed in (it comes from react-oidc's useAuth); failures still proceed to clear + redirect.
export function signOutClean(removeUser: () => Promise<void>): Promise<void> {
  return removeUser()
    .catch(() => undefined)
    .then(() => {
      clearLocalAuthState();
      signOutRedirect();
    });
}

// The token the BACKEND wants, per IdP.
//
//   cognito / entra  the access token — Entra can mint one whose `aud` is the
//                    XecGuard API (that is what the delegated scopes bought).
//   google           the ID token. Google CANNOT issue an access token for your
//                    own API; its access tokens address Google APIs. The GCP M2
//                    middleware therefore verifies an ID token
//                    (verify_oauth2_token(..., audience=client_id)), and sending
//                    an access token there fails every request with 401.
//
// The GIS shim already puts the ID token in `access_token` so the ~30 existing
// call sites keep working unchanged; this helper is where that choice is
// written down, and the thing to call from any NEW code.
export function apiToken(user?: { access_token?: string; id_token?: string }): string | undefined {
  if (!user) return undefined;
  return isGoogle ? (user.id_token ?? user.access_token) : user.access_token;
}

// Auth-only header (no license) — for endpoints that must not depend on a license we
// don't have yet, e.g. GET /me itself.
export function baseAuthHeaders(token?: string): Record<string, string> | undefined {
  if (!token) return undefined;
  return isGatewayIdp ? { Authorization: `Bearer ${token}` } : { "X-Identity-Token": token };
}

// Management-plane auth header for the user (NOT the /scan xgs_ service-token path,
// which sets its own Authorization header in postScan.ts).
// - Entra/APIM and Google/GCP: `Authorization: Bearer <token>` + `X-License-Id`
//   (ADR-008). The GCP middleware reads the same two headers as APIM.
// - Cognito/AWS: the user token in `X-Identity-Token`.
export function authHeaders(token?: string): Record<string, string> | undefined {
  const base = baseAuthHeaders(token);
  if (!base) return undefined;
  if (isGatewayIdp) {
    const licenseId = getActiveLicenseId();
    if (licenseId) return { ...base, "X-License-Id": licenseId };
  }
  return base;
}
